import { useState, useEffect, useMemo, useCallback, useRef, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { fetchTagsForContext, type TagDefinition } from '@/db/tags';
import { createPortal } from 'react-dom';
import {
  X,
  Plus,
  User,
  Tag,
  Flag,
  FolderKanban,
  List,
  FileText,
  Clock,
  ChevronDown,
  Loader2,
  Check,
} from 'lucide-react';
import clsx from 'clsx';
import type { Task, Priority, CustomFieldValue } from '@/types';
import { CustomFieldsSection } from '@/components/fields/CustomFieldEditor';
import { DateRangePicker } from '@/components/shared/inline-editors';
import { useAppStore } from '@/store/appStore';
import { useSpaces } from '@/hooks/useSpaces';
import { useTaskEnrichment } from '@/hooks/useTaskEnrichment';
import { useDatabase } from '@/context/DatabaseContext';
import { useOrganization } from '@/context/OrganizationContext';
import { useTaskActions } from '@/hooks/useTaskActions';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useDropdownPosition } from '@/hooks/useDropdownPosition';
import { generateA11yId, trapFocus } from '@/utils/accessibility';
import { handleError } from '@/utils/errorHandler';
import { getOrgMembers, type OrgMember } from '@/db/organizations';
import { getInitials, getAvatarColor } from '@/utils/formatters';
import { DEFAULT_TASK_STATUS } from '@/constants/taskStatuses';
import { parseDuration } from '@/utils/duration';

interface CreateTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TAG_COLORS: Record<string, string> = {
  'accent-violet': '#8b5cf6',
  'accent-blue': '#3b82f6',
  'accent-emerald': '#10b981',
  'accent-rose': '#f43f5e',
  'accent-amber': '#f59e0b',
  'accent-cyan': '#06b6d4',
  'text-quaternary': '#6b7280',
};

function tagHexFor(color?: string | null) {
  return (color && TAG_COLORS[color]) ? TAG_COLORS[color] : '#6b7280';
}

const PRIORITIES: { id: Priority; label: string; color: string; iconColor: string }[] = [
  { id: 'urgent', label: 'Urgente', color: 'text-accent-rose bg-accent-rose/10', iconColor: 'text-accent-rose' },
  { id: 'high', label: 'Alta', color: 'text-accent-amber bg-accent-amber/10', iconColor: 'text-accent-orange' },
  { id: 'normal', label: 'Normale', color: 'text-accent-blue bg-accent-blue/10', iconColor: 'text-accent-amber' },
  { id: 'low', label: 'Bassa', color: 'text-text-tertiary bg-surface-tertiary', iconColor: 'text-accent-cyan' },
];

// ---- Portal Select Component ----
interface PortalSelectProps {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  placeholder: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

function PortalSelect({ label, value, options, onChange, placeholder, icon, disabled }: PortalSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownStyle = useDropdownPosition(isOpen, triggerRef, {
    offset: 4,
    minWidth: 180,
    matchTriggerWidth: true,
  });

  useClickOutside([dropdownRef, triggerRef], () => setIsOpen(false), isOpen);

  const selectedLabel = options.find(o => o.value === value)?.label;

  return (
    <div className="relative">
      <label className="block text-[10px] font-medium text-text-tertiary mb-1">{label}</label>
      <div ref={triggerRef}>
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={clsx(
            'w-full pl-8 pr-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-left transition-all flex items-center relative',
            disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-border-secondary cursor-pointer',
            isOpen && 'border-accent-violet ring-1 ring-accent-violet',
            value ? 'text-text-primary' : 'text-text-quaternary'
          )}
        >
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2">{icon}</span>
          <span className="truncate flex-1">{selectedLabel || placeholder}</span>
          <ChevronDown className={clsx('w-3 h-3 text-text-quaternary shrink-0 transition-transform', isOpen && 'rotate-180')} />
        </button>
      </div>

      {isOpen && dropdownStyle && createPortal(
        <div
          ref={dropdownRef}
          style={dropdownStyle}
          className="py-1 rounded-lg shadow-2xl animate-fade-in border border-border-primary/70 bg-surface-primary max-h-[200px] overflow-y-auto"
        >
          <button
            type="button"
            onClick={() => { onChange(''); setIsOpen(false); }}
            className={clsx(
              'w-full px-3 py-1.5 text-[11px] text-left hover:bg-white/10 transition-colors text-text-quaternary',
              !value && 'bg-white/10'
            )}
          >
            {placeholder}
          </button>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
              className={clsx(
                'w-full px-3 py-1.5 text-[11px] text-left hover:bg-white/10 transition-colors flex items-center justify-between',
                opt.value === value && 'bg-white/10'
              )}
            >
              <span className="text-text-primary truncate">{opt.label}</span>
              {opt.value === value && <Check className="w-3 h-3 text-accent-violet shrink-0" />}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

export function CreateTaskModal({ isOpen, onClose }: CreateTaskModalProps) {
  const { tasks } = useDatabase();
  const { spaces, containersMap, listsMap } = useSpaces();
  const { currentOrg } = useOrganization();
  const { uniqueValues } = useTaskEnrichment({
    tasks,
    containersMap: containersMap || new Map(),
  });
  const filters = useAppStore((state) => state.filters);
  const { handleCreateTask: onCreateTask } = useTaskActions();

  // Accessibility
  const modalRef = useRef<HTMLDivElement>(null);
  const titleId = useMemo(() => generateA11yId('create-task-modal-title'), []);

  // Extract space/folder/list from path
  const pathParts = filters.path ? filters.path.split('/') : [];
  const defaultSpace = pathParts[0] || null;
  const defaultFolder = pathParts.length > 1 ? pathParts[1] : null;
  const defaultList = pathParts.length > 2 ? pathParts[2] : null;
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [selectedSpace, setSelectedSpace] = useState<string>(defaultSpace || '');
  const _selectedFolder = defaultFolder || '';
  const [selectedList, setSelectedList] = useState<string>(defaultList || '');
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [priority, setPriority] = useState<Priority | null>(null);
  const [dueDate, setDueDate] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<number | null>(null);
  const [timeEstimated, setTimeEstimated] = useState<string>('');
  const [customFields, setCustomFields] = useState<Record<string, CustomFieldValue>>({});
  const [isCreating, setIsCreating] = useState(false);
  const [contextTags, setContextTags] = useState<TagDefinition[]>([]);

  // Org members for assignees
  const [orgMembers, setOrgMembers] = useState<OrgMember[]>([]);

  // Assignee dropdown state
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const assigneeTriggerRef = useRef<HTMLDivElement>(null);
  const assigneeDropdownRef = useRef<HTMLDivElement>(null);
  const assigneeInputRef = useRef<HTMLInputElement>(null);
  const assigneeDropdownStyle = useDropdownPosition(showAssigneeDropdown, assigneeTriggerRef, {
    offset: 4,
    minWidth: 220,
    matchTriggerWidth: true,
  });

  // Tag dropdown state
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const tagTriggerRef = useRef<HTMLDivElement>(null);
  const tagDropdownRef = useRef<HTMLDivElement>(null);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const tagDropdownStyle = useDropdownPosition(showTagDropdown, tagTriggerRef, {
    offset: 4,
    minWidth: 220,
    matchTriggerWidth: true,
  });

  // Priority dropdown state
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const priorityTriggerRef = useRef<HTMLDivElement>(null);
  const priorityDropdownRef = useRef<HTMLDivElement>(null);
  const priorityDropdownStyle = useDropdownPosition(showPriorityDropdown, priorityTriggerRef, {
    offset: 4,
    minWidth: 160,
    matchTriggerWidth: true,
  });

  // Click-outside handlers
  useClickOutside([assigneeDropdownRef, assigneeTriggerRef], () => { setShowAssigneeDropdown(false); setAssigneeSearch(''); }, showAssigneeDropdown);
  useClickOutside([tagDropdownRef, tagTriggerRef], () => { setShowTagDropdown(false); setTagSearch(''); }, showTagDropdown);
  useClickOutside([priorityDropdownRef, priorityTriggerRef], () => setShowPriorityDropdown(false), showPriorityDropdown);

  // Load org members
  useEffect(() => {
    if (currentOrg?.id) {
      getOrgMembers(currentOrg.id).then(members => {
        setOrgMembers(members);
      }).catch((err) => handleError(err, { level: 'warning', message: 'Errore nel caricamento dei membri' }));
    }
  }, [currentOrg?.id]);

  // Fetch context tags when space/list selection changes
  useEffect(() => {
    if (!selectedSpace) { setContextTags([]); return; }
    // selectedSpace is now a space ID
    const space = spaces.find(s => s.id === selectedSpace);
    if (!space) { setContextTags([]); return; }
    const spaceId = space.id;
    // selectedList is now a list ID
    const listId = selectedList || undefined;
    fetchTagsForContext({ orgId: currentOrg?.id, spaceId, listId })
      .then(setContextTags)
      .catch(() => {});
  }, [selectedSpace, selectedList, spaces, listsMap, currentOrg?.id]);

  // Focus assignee input when dropdown opens
  useEffect(() => {
    if (showAssigneeDropdown && assigneeInputRef.current) {
      assigneeInputRef.current.focus();
    }
  }, [showAssigneeDropdown]);

  // Focus tag input when dropdown opens
  useEffect(() => {
    if (showTagDropdown && tagInputRef.current) {
      tagInputRef.current.focus();
    }
  }, [showTagDropdown]);

  // Available assignees: use user_id (UUID) — matches what's stored in task.assignees
  const availableAssignees = useMemo(() => {
    return orgMembers.map(m => m.user_id);
  }, [orgMembers]);

  const resolveAssigneeName = useCallback((id: string) => {
    const m = orgMembers.find(m => m.user_id === id);
    return m ? (m.display_name || m.email || m.user_id.slice(0, 8)) : id;
  }, [orgMembers]);

  const filteredAssignees = availableAssignees.filter(
    a => !selectedAssignees.includes(a) && resolveAssigneeName(a).toLowerCase().includes(assigneeSearch.toLowerCase())
  );

  const allAvailableTags = useMemo(() => {
    const contextTagNames = new Set(contextTags.map(t => t.name));
    const extraTagNames = uniqueValues.tags.filter(t => !contextTagNames.has(t));
    return { contextTags, extraTagNames };
  }, [contextTags, uniqueValues.tags]);

  const filteredContextTags = allAvailableTags.contextTags.filter(
    t => !selectedTags.includes(t.name) && t.name.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const filteredExtraTags = allAvailableTags.extraTagNames.filter(
    t => !selectedTags.includes(t) && t.toLowerCase().includes(tagSearch.toLowerCase())
  );

  // Get available lists based on selected space (all lists: root + inside folders)
  // Returns array of { id, name } for ID-based selection
  const availableLists = useMemo(() => {
    const space = spaces.find((s) => s.id === selectedSpace);
    if (!space) return [] as { id: string; name: string }[];
    const folderLists = space.folders.flatMap((f) => f.lists.map((l) => ({ id: l.id, name: l.name })));
    const rootLists = (space.rootLists || []).map((l) => ({ id: l.id, name: l.name }));
    return [...rootLists, ...folderLists];
  }, [spaces, selectedSpace]);

  // Close on escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Reset form when opening
  useEffect(() => {
    if (isOpen) {
      setName('');
      setContent('');
      setSelectedSpace(defaultSpace || spaces[0]?.name || '');
      setSelectedList(defaultList || '');
      setSelectedAssignees([]);
      setSelectedTags([]);
      setContextTags([]);
      setPriority(null);
      setDueDate(null);
      setStartDate(null);
      setTimeEstimated('');
      setCustomFields({});
      setIsCreating(false);
    }
  }, [isOpen, defaultSpace, defaultFolder, defaultList, spaces]);

  // Focus trapping
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const cleanup = trapFocus(modalRef.current);
      return cleanup;
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || isCreating) return;

    setIsCreating(true);
    try {
      // selectedSpace and selectedList are now IDs
      // Set spaceId directly to the most specific container (list > space)
      const containerId = selectedList || selectedSpace || undefined;
      const newTask: Partial<Task> = {
        name: name.trim(),
        content: content.trim(),
        status: DEFAULT_TASK_STATUS,
        spaceId: containerId as Task['spaceId'],
        assignees: selectedAssignees,
        tags: selectedTags,
        priority,
        dueDate,
        startDate,
        timeEstimated: timeEstimated ? parseDuration(timeEstimated) : null,
        attachments: [],
        comments: [],
        assignedComments: 0,
        checklists: {},
        parentId: null,
        rolledUpTime: null,
        customFields: Object.keys(customFields).length > 0 ? customFields : undefined,
      };

      await onCreateTask(newTask);
      onClose();
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nella creazione del task' });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCustomFieldChange = (fieldId: string, value: CustomFieldValue) => {
    setCustomFields((prev) => ({ ...prev, [fieldId]: value }));
  };

  const toggleAssignee = (assignee: string) => {
    setSelectedAssignees((prev) =>
      prev.includes(assignee) ? prev.filter((a) => a !== assignee) : [...prev, assignee]
    );
  };

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleTagKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      if (filteredContextTags.length > 0) {
        toggleTag(filteredContextTags[0].name);
        setTagSearch('');
      } else if (filteredExtraTags.length > 0) {
        toggleTag(filteredExtraTags[0]);
        setTagSearch('');
      } else if (tagSearch.trim() && !selectedTags.includes(tagSearch.trim())) {
        setSelectedTags(prev => [...prev, tagSearch.trim()]);
        setTagSearch('');
      }
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setShowTagDropdown(false);
      setTagSearch('');
    }
  };

  const handleAssigneeKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredAssignees.length > 0) {
        toggleAssignee(filteredAssignees[0]);
        setAssigneeSearch('');
      }
    } else if (e.key === 'Escape') {
      setShowAssigneeDropdown(false);
      setAssigneeSearch('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative w-full max-w-2xl max-h-[90vh] bg-surface-secondary border border-border-primary rounded-xl shadow-2xl animate-scale-in flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-primary">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-accent-violet/10 flex items-center justify-center">
              <Plus className="w-4 h-4 text-accent-violet" />
            </div>
            <h2 id={titleId} className="text-[15px] font-semibold text-text-primary">
              Nuovo Task
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Chiudi finestra di dialogo"
            className="p-1.5 rounded-md hover:bg-surface-hover transition-colors text-text-quaternary hover:text-text-tertiary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-4">
            {/* Task Name */}
            <div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nome del task"
                className="w-full px-0 py-2 bg-transparent border-0 border-b-2 border-border-primary text-[16px] font-medium text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:outline-none transition-colors"
                autoFocus
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-[10px] font-medium text-text-tertiary mb-1">Descrizione</label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-3.5 h-3.5 text-text-quaternary pointer-events-none" />
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Descrizione..."
                  rows={2}
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet transition-all resize-none"
                />
              </div>
            </div>

            {/* Location - Portal Selects */}
            <div className="grid grid-cols-2 gap-2">
              <PortalSelect
                label="Space"
                value={selectedSpace}
                options={spaces.map(s => ({ value: s.id, label: s.name }))}
                onChange={(v) => { setSelectedSpace(v); setSelectedList(''); }}
                placeholder="Space"
                icon={<FolderKanban className="w-3.5 h-3.5 text-text-quaternary" />}
              />
              <PortalSelect
                label="Lista"
                value={selectedList}
                options={availableLists.map(l => ({ value: l.id, label: l.name }))}
                onChange={setSelectedList}
                placeholder={selectedSpace ? 'Nessuna (nello spazio)' : 'Lista'}
                icon={<List className="w-3.5 h-3.5 text-text-quaternary" />}
                disabled={!selectedSpace}
              />
            </div>

            {/* Info: task will be created directly in the space */}
            {selectedSpace && !selectedList && (
              <p className="text-[11px] text-text-tertiary -mt-2">
                Il task verrà creato direttamente nello spazio «{spaces.find(s => s.id === selectedSpace)?.name || selectedSpace}»
              </p>
            )}

            {/* Dates & Estimate */}
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <label className="block text-[10px] font-medium text-text-tertiary mb-1">Date</label>
                <DateRangePicker
                  startDate={startDate}
                  endDate={dueDate}
                  onChangeStart={setStartDate}
                  onChangeEnd={setDueDate}
                  showTime={false}
                  triggerClassName={clsx(
                    'w-full flex items-center gap-2 pl-3 pr-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] hover:border-border-secondary transition-all cursor-pointer',
                    startDate || dueDate ? 'text-text-primary' : 'text-text-quaternary'
                  )}
                />
              </div>

              <div>
                <label className="block text-[10px] font-medium text-text-tertiary mb-1">Stima</label>
                <div className="relative">
                  <Clock className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-quaternary pointer-events-none" />
                  <input
                    type="text"
                    value={timeEstimated}
                    onChange={(e) => setTimeEstimated(e.target.value)}
                    placeholder="es. 1h 30m"
                    className="w-full pl-8 pr-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-text-primary placeholder:text-text-quaternary focus:border-accent-violet focus:ring-1 focus:ring-accent-violet transition-all"
                  />
                </div>
              </div>
            </div>

            {/* Assignees, Tags, Priority */}
            <div className="grid grid-cols-3 gap-2">
              {/* Assignees */}
              <div>
                <label className="block text-[10px] font-medium text-text-tertiary mb-1">Assegnatari</label>
                <div ref={assigneeTriggerRef}>
                  <button
                    type="button"
                    onClick={() => setShowAssigneeDropdown(!showAssigneeDropdown)}
                    className={clsx(
                      'w-full pl-8 pr-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-left hover:border-border-secondary transition-all flex items-center relative',
                      showAssigneeDropdown && 'border-accent-violet ring-1 ring-accent-violet'
                    )}
                  >
                    <User className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-quaternary" />
                    <span className={clsx('truncate', selectedAssignees.length > 0 ? 'text-text-primary' : 'text-text-quaternary')}>
                      {selectedAssignees.length > 0 ? `${selectedAssignees.length} assegnatari` : 'Assegnatari'}
                    </span>
                    <ChevronDown className={clsx('w-3 h-3 text-text-quaternary shrink-0 ml-auto transition-transform', showAssigneeDropdown && 'rotate-180')} />
                  </button>
                </div>

                {showAssigneeDropdown && assigneeDropdownStyle && createPortal(
                  <div
                    ref={assigneeDropdownRef}
                    style={assigneeDropdownStyle}
                    className="rounded-lg shadow-2xl animate-fade-in border border-border-primary/70 bg-surface-primary overflow-hidden"
                  >
                    <div className="p-2 border-b border-border-primary/50">
                      <input
                        ref={assigneeInputRef}
                        type="text"
                        value={assigneeSearch}
                        onChange={(e) => setAssigneeSearch(e.target.value)}
                        onKeyDown={handleAssigneeKeyDown}
                        placeholder="Cerca membro..."
                        className="w-full px-2.5 py-1.5 text-[11px] bg-surface-tertiary border border-border-primary/50 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary placeholder-text-quaternary"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto py-1">
                      {filteredAssignees.map((assignee) => {
                        const dn = resolveAssigneeName(assignee);
                        return (
                        <button
                          key={assignee}
                          type="button"
                          onClick={() => { toggleAssignee(assignee); setAssigneeSearch(''); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10 transition-colors"
                        >
                          <div className={clsx('w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white', getAvatarColor(dn))}>
                            {getInitials(dn)}
                          </div>
                          <span className="text-[11px] text-text-primary truncate">{dn}</span>
                        </button>
                        );
                      })}
                      {/* Already selected */}
                      {selectedAssignees.filter(a => resolveAssigneeName(a).toLowerCase().includes(assigneeSearch.toLowerCase())).map((assignee) => {
                        const dn = resolveAssigneeName(assignee);
                        return (
                        <button
                          key={`sel-${assignee}`}
                          type="button"
                          onClick={() => toggleAssignee(assignee)}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
                        >
                          <div className={clsx('w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-semibold text-white', getAvatarColor(dn))}>
                            {getInitials(dn)}
                          </div>
                          <span className="text-[11px] text-accent-violet truncate flex-1">{dn}</span>
                          <Check className="w-3 h-3 text-accent-violet shrink-0" />
                        </button>
                        );
                      })}
                      {filteredAssignees.length === 0 && selectedAssignees.length === 0 && !assigneeSearch && (
                        <div className="px-3 py-2 text-[11px] text-text-quaternary text-center">
                          Nessun membro trovato
                        </div>
                      )}
                    </div>
                  </div>,
                  document.body
                )}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-[10px] font-medium text-text-tertiary mb-1">Tag</label>
                <div ref={tagTriggerRef}>
                  <button
                    type="button"
                    onClick={() => setShowTagDropdown(!showTagDropdown)}
                    className={clsx(
                      'w-full pl-8 pr-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-left hover:border-border-secondary transition-all flex items-center relative',
                      showTagDropdown && 'border-accent-violet ring-1 ring-accent-violet'
                    )}
                  >
                    <Tag className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-quaternary" />
                    <span className={clsx('truncate', selectedTags.length > 0 ? 'text-text-primary' : 'text-text-quaternary')}>
                      {selectedTags.length > 0 ? `${selectedTags.length} tag` : 'Tag'}
                    </span>
                    <ChevronDown className={clsx('w-3 h-3 text-text-quaternary shrink-0 ml-auto transition-transform', showTagDropdown && 'rotate-180')} />
                  </button>
                </div>

                {showTagDropdown && tagDropdownStyle && createPortal(
                  <div
                    ref={tagDropdownRef}
                    style={tagDropdownStyle}
                    className="rounded-lg shadow-2xl animate-fade-in border border-border-primary/70 bg-surface-primary overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="p-2 border-b border-border-primary/50">
                      <input
                        ref={tagInputRef}
                        type="text"
                        value={tagSearch}
                        onChange={(e) => setTagSearch(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                        placeholder="Cerca o crea tag..."
                        className="w-full px-2.5 py-1.5 text-[11px] bg-surface-tertiary border border-border-primary/50 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-violet text-text-primary placeholder-text-quaternary"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto py-1">
                      {/* Selected tags */}
                      {selectedTags.filter(t => t.toLowerCase().includes(tagSearch.toLowerCase())).map((tag) => (
                        <button
                          key={`sel-${tag}`}
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTag(tag); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left bg-accent-violet/10 hover:bg-accent-violet/20 transition-colors"
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-accent-violet shrink-0" />
                          <span className="text-[11px] text-accent-violet truncate flex-1">{tag}</span>
                          <X className="w-3 h-3 text-text-quaternary shrink-0" />
                        </button>
                      ))}
                      {/* Context tags (from DB, with color) */}
                      {filteredContextTags.map((tag) => {
                        const hex = tagHexFor(tag.color);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTag(tag.name); setTagSearch(''); }}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10 transition-colors"
                          >
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: hex }} />
                            <span className="text-[11px] text-text-primary truncate">{tag.name}</span>
                          </button>
                        );
                      })}
                      {/* Extra tags (from existing tasks, plain) */}
                      {filteredExtraTags.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleTag(tag); setTagSearch(''); }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white/10 transition-colors"
                        >
                          <Tag className="w-3 h-3 text-accent-violet shrink-0" />
                          <span className="text-[11px] text-text-primary truncate">{tag}</span>
                        </button>
                      ))}
                      {/* Create new tag */}
                      {tagSearch.trim() && !filteredContextTags.some(t => t.name.toLowerCase() === tagSearch.toLowerCase()) && !filteredExtraTags.some(t => t.toLowerCase() === tagSearch.toLowerCase()) && !selectedTags.includes(tagSearch.trim()) && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedTags(prev => [...prev, tagSearch.trim()]);
                            setTagSearch('');
                          }}
                          className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-accent-violet/10 transition-colors text-accent-violet"
                        >
                          <Plus className="w-3 h-3 shrink-0" />
                          <span className="text-[11px] font-medium">Crea "{tagSearch.trim()}"</span>
                        </button>
                      )}
                      {filteredContextTags.length === 0 && filteredExtraTags.length === 0 && selectedTags.length === 0 && !tagSearch && (
                        <div className="px-3 py-2 text-[11px] text-text-quaternary text-center">
                          Nessun tag. Digita per crearne uno.
                        </div>
                      )}
                    </div>
                  </div>,
                  document.body
                )}
              </div>

              {/* Priority */}
              <div>
                <label className="block text-[10px] font-medium text-text-tertiary mb-1">Priorità</label>
                <div ref={priorityTriggerRef}>
                  <button
                    type="button"
                    onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                    className={clsx(
                      'w-full pl-8 pr-3 py-2 rounded-lg bg-surface-tertiary border border-border-primary text-[12px] text-left hover:border-border-secondary transition-all flex items-center relative',
                      showPriorityDropdown && 'border-accent-violet ring-1 ring-accent-violet'
                    )}
                  >
                    <Flag className={clsx('absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5', priority ? PRIORITIES.find(p => p.id === priority)?.iconColor : 'text-text-quaternary')} />
                    <span className={clsx(priority ? 'text-text-primary' : 'text-text-quaternary')}>
                      {priority ? PRIORITIES.find((p) => p.id === priority)?.label : 'Priorità'}
                    </span>
                    <ChevronDown className={clsx('w-3 h-3 text-text-quaternary shrink-0 ml-auto transition-transform', showPriorityDropdown && 'rotate-180')} />
                  </button>
                </div>

                {showPriorityDropdown && priorityDropdownStyle && createPortal(
                  <div
                    ref={priorityDropdownRef}
                    style={priorityDropdownStyle}
                    className="py-1 rounded-lg shadow-2xl animate-fade-in border border-border-primary/70 bg-surface-primary z-[60]"
                  >
                    <button
                      type="button"
                      onClick={() => { setPriority(null); setShowPriorityDropdown(false); }}
                      className={clsx(
                        'w-full px-3 py-1.5 text-[11px] text-left hover:bg-white/10 transition-colors text-text-quaternary',
                        !priority && 'bg-white/10'
                      )}
                    >
                      Nessuna
                    </button>
                    {PRIORITIES.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setPriority(p.id); setShowPriorityDropdown(false); }}
                        className={clsx(
                          'w-full px-3 py-1.5 text-[11px] text-left hover:bg-white/10 transition-colors flex items-center gap-2',
                          priority === p.id && 'bg-white/10'
                        )}
                      >
                        <Flag className={clsx('w-3 h-3', p.iconColor)} />
                        <span className={clsx('font-medium', p.color, 'px-1.5 py-0.5 rounded text-[10px]')}>
                          {p.label}
                        </span>
                      </button>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
            </div>

            {/* Selected Assignees & Tags Preview */}
            {(selectedAssignees.length > 0 || selectedTags.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {selectedAssignees.map((assignee) => (
                  <span
                    key={assignee}
                    className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded bg-accent-blue/10 text-accent-blue text-[10px] font-medium"
                  >
                    <User className="w-2.5 h-2.5" />
                    {resolveAssigneeName(assignee)}
                    <button type="button" onClick={() => toggleAssignee(assignee)} className="hover:text-accent-rose">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                {selectedTags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded bg-accent-emerald/10 text-accent-emerald text-[10px] font-medium"
                  >
                    <Tag className="w-2.5 h-2.5" />
                    {tag}
                    <button type="button" onClick={() => toggleTag(tag)} className="hover:text-accent-rose">
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Custom Fields */}
            {selectedSpace && (
              <CustomFieldsSection
                task={{
                  spaceId: selectedList || selectedSpace,
                }}
                customFields={customFields}
                onCustomFieldChange={handleCustomFieldChange}
                disabled={false}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-3 border-t border-border-primary bg-surface-tertiary/30">
            <p className="text-[10px] text-text-quaternary hidden sm:flex items-center gap-1">
              <kbd className="kbd">⌘</kbd>+<kbd className="kbd">↵</kbd>
            </p>
            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-text-secondary hover:bg-surface-hover transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={!name.trim() || isCreating}
                className={clsx(
                  'px-4 py-1.5 rounded-lg text-[11px] font-medium text-white transition-all flex items-center gap-1.5',
                  name.trim() && !isCreating
                    ? 'bg-accent-violet hover:bg-accent-violet-light shadow-lg shadow-accent-violet/20'
                    : 'bg-surface-tertiary text-text-quaternary cursor-not-allowed'
                )}
              >
                {isCreating && <Loader2 className="w-3 h-3 animate-spin" />}
                {isCreating ? 'Creazione...' : 'Crea Task'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
